import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useGetProductsQuery,
    useGetQuizAttemptsQuery,
    useGetQuizStatsQuery,
} from '../product_knowledge/productKnowledgeApiSlice';
import { Container, Message, Icon, Button, Header } from 'semantic-ui-react';

import QuizDashboardSkeleton from './QuizDashboardSkeleton';
import styles from './QuizDashboard.module.css';
import { NAV } from '../../navLabels';
import { useBreakpoint } from '../../hooks/useBreakpoint';

// LG U+ categories are stored as lgu_<kind> (load_lgu_products); Apple ones display as-is.
const CATEGORY_LABELS = {
    lgu_plan: 'U+ 요금제', lgu_addon: 'U+ 부가서비스', lgu_bundle: 'U+ 결합 할인',
    lgu_card: 'U+ 제휴카드', lgu_discount: 'U+ 요금 할인', lgu_internet: 'U+ 인터넷',
    lgu_iptv: 'U+tv', lgu_smarthome: 'U+ 스마트홈', lgu_smarthome_device: 'U+ 스마트홈 기기',
    lgu_tradein: 'U+ 중고폰 보상', lgu_guide: 'U+ 판매 가이드',
    // Apple's are stored as lowercase codes; show the product-line names.
    mac: 'Mac', ipad: 'iPad', iphone: 'iPhone', watch: 'Apple Watch', airpods: 'AirPods',
    'apple-tv': 'Apple TV', vision: 'Apple Vision Pro', service: 'Apple 서비스',
};
// Phones list the first few products per category under 전체; the chip shows the rest.
const MOBILE_PER_CATEGORY = 5;
const categoryLabel = cat => CATEGORY_LABELS[cat] || cat;

const QuizDashboard = () => {
    const navigate = useNavigate();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const { isMobile } = useBreakpoint();

    // ---- Data Fetching ----
    const {
        data: productsData,
        isLoading: isProductsLoading,
        isError: isProductsError,
        error: productsError,
        refetch: refetchProducts,
    } = useGetProductsQuery({
        page_size: 100,
        has_quiz: 'true',
        release_year_gte: new Date().getFullYear() - 2,
    });

    const {
        data: attemptsData,
        isLoading: isAttemptsLoading,
        isError: isAttemptsError,
    } = useGetQuizAttemptsQuery({ page_size: 10 });

    const {
        data: statsData,
        isLoading: isStatsLoading,
        isError: isStatsError,
    } = useGetQuizStatsQuery();

    // ---- Derived Data ----
    const allProducts = useMemo(() => productsData?.results || [], [productsData]);
    const recentAttempts = useMemo(() => attemptsData?.results?.slice(0, 5) || [], [attemptsData]);

    const categories = useMemo(() => {
        const unique = [...new Set(allProducts.map(p => p.category).filter(Boolean))];
        return unique.sort();
    }, [allProducts]);

    // Group products by category, then filter if a category is selected
    const productsByCategory = useMemo(() => {
        const grouped = {};
        const filtered = selectedCategory
            ? allProducts.filter(p => p.category === selectedCategory)
            : allProducts;
        filtered.forEach(p => {
            const cat = p.category || '기타';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(p);
        });
        return grouped;
    }, [allProducts, selectedCategory]);

    const stats = useMemo(() => {
        if (!statsData) return { totalAttempts: 0, accuracy: 0, currentStreak: 0, mcAccuracy: 0, oeAccuracy: 0 };
        return {
            // "풀이한 문제" counts questions, not attempts: re-answering one question 25 times
            // in practice is still one question (#249). total_attempts stays the activity count.
            totalAttempts: statsData.questions_answered ?? statsData.total_attempts,
            accuracy: statsData.accuracy,
            currentStreak: statsData.current_streak,
            mcAccuracy: statsData.mc_accuracy,
            oeAccuracy: statsData.oe_accuracy,
        };
    }, [statsData]);

    const attemptsByProduct = useMemo(() => statsData?.by_product || {}, [statsData]);

    const startQuiz = useCallback((mode, productId = null) => {
        const params = new URLSearchParams();
        params.set('mode', mode);
        if (productId) params.set('product_id', productId);
        navigate(`/quiz/session?${params.toString()}`);
    }, [navigate]);

    const getAccuracyColor = (accuracy) => {
        if (accuracy >= 80) return '#34C759';
        if (accuracy >= 50) return '#FF9500';
        return '#FF3B30';
    };

    // ---- Loading ----
    if (isProductsLoading || isAttemptsLoading || isStatsLoading) {
        return <QuizDashboardSkeleton />;
    }

    if (isProductsError || isAttemptsError || isStatsError) return null;

    return (
        <div className={styles.page}>
            {/* ===== Header Bar ===== */}
            <div className={styles.headerBar}>
                <Container>
                    <div className={styles.headerInner}>
                        <div className={styles.headerLeft}>
                            <div className={`${styles.headerIcon} header-icon-desktop-only`}>
                                <Icon name="student" style={{ margin: 0, color: '#007AFF', fontSize: '18px' }} />
                            </div>
                            <div>
                                <h2 className={styles.headerTitle}>{NAV.QUIZ}</h2>
                                <p className={styles.headerSubtitle}>{NAV.QUIZ_SUBTITLE}</p>
                            </div>
                        </div>
                    </div>
                </Container>
            </div>

            {/* ===== Main Content — all inside Container ===== */}
            <Container className={styles.mainContainer}>

                {/* Quick Action Cards */}
                <div className={styles.quickActionsRow}>
                    <div
                        className={styles.actionCardPrimary}
                        onClick={() => startQuiz('practice')}
                        role="button"
                        tabIndex={0}
                        aria-label="연습 모드 시작"
                        onKeyDown={(e) => e.key === 'Enter' && startQuiz('practice')}
                    >
                        <div className={styles.actionCardIconPrimary}>
                            <Icon name="play" style={{ margin: 0, color: '#fff', fontSize: '20px' }} />
                        </div>
                        <h3 className={styles.actionCardTitle}>연습 모드</h3>
                        <p className={styles.actionCardSub}>문제별 즉시 피드백으로<br/>약점을 파악하세요</p>
                        <Icon name="arrow right" className={styles.actionCardArrow} />
                    </div>

                    <div
                        className={styles.actionCardSecondary}
                        onClick={() => startQuiz('test')}
                        role="button"
                        tabIndex={0}
                        aria-label="실전 테스트 시작"
                        onKeyDown={(e) => e.key === 'Enter' && startQuiz('test')}
                    >
                        <div className={styles.actionCardIconSecondary}>
                            <Icon name="clipboard list" style={{ margin: 0, color: '#1d1d1f', fontSize: '20px' }} />
                        </div>
                        <h3 className={styles.actionCardTitle}>실전 테스트</h3>
                        <p className={styles.actionCardSub}>시간 제한 환경에서<br/>실력을 측정하세요</p>
                        <Icon name="arrow right" className={styles.actionCardArrow} />
                    </div>
                </div>

                {/* Stats Row */}
                <div className={styles.statsRow}>
                    <div className={styles.statCard}>
                        <div className={styles.statCardValue}>{stats.totalAttempts}</div>
                        <div className={styles.statCardLabel}>풀이한 문제</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statCardValue}>{stats.accuracy}%</div>
                        <div className={styles.statCardLabel}>정답률</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statCardValue}>{stats.mcAccuracy}%</div>
                        <div className={styles.statCardLabel}>객관식 정답률</div>
                    </div>
                    {stats.currentStreak > 0 ? (
                        <div className={styles.statCardAccent} onClick={() => startQuiz('practice')}>
                            <div className={styles.streakIcon}>🔥</div>
                            <div className={styles.statCardValue}>{stats.currentStreak}일</div>
                            <div className={styles.statCardLabel}>연속 학습</div>
                        </div>
                    ) : (
                        <div className={styles.statCard}>
                            <div className={styles.statCardValue}>{stats.oeAccuracy}%</div>
                            <div className={styles.statCardLabel}>서술형 정답률</div>
                        </div>
                    )}
                </div>

                {/* ===== Products by Category (horizontal scroll rows) ===== */}
                <Header as="h3" className={styles.productSectionTitle}>제품별 학습</Header>

                {/* Category filter chips */}
                <div className={styles.categoryChips}>
                    <button
                        className={selectedCategory === null ? styles.categoryChipActive : styles.categoryChip}
                        onClick={() => setSelectedCategory(null)}
                    >
                        전체
                    </button>
                    {categories.map(cat => (
                        <button
                            key={cat}
                            className={selectedCategory === cat ? styles.categoryChipActive : styles.categoryChip}
                            onClick={() => setSelectedCategory(cat)}
                        >
                            {categoryLabel(cat)}
                        </button>
                    ))}
                </div>

                {/* Product scroll rows grouped by category */}
                {Object.keys(productsByCategory).length === 0 ? (
                    <div className={styles.emptyState}>
                        <Icon name="search" size="big" style={{ color: '#d2d2d7', marginBottom: 12 }} />
                        <p style={{ color: '#86868b', fontSize: 15 }}>학습 가능한 제품이 없습니다.</p>
                    </div>
                ) : (
                    Object.entries(productsByCategory).map(([category, products]) => (
                        <div key={category} className={styles.productScrollSection}>
                            <div className={styles.productScrollHeader}>
                                <h4 className={styles.productScrollLabel}>{categoryLabel(category)}</h4>
                                <span className={styles.productScrollCount}>{products.length}개 제품</span>
                            </div>
                            <div className={styles.productScrollTrack}>
                                {(isMobile && !selectedCategory ? products.slice(0, MOBILE_PER_CATEGORY) : products).map(product => {
                                    const pStats = attemptsByProduct?.[product.id];
                                    const accuracy = pStats && pStats.total > 0
                                        ? Math.round((pStats.correct / pStats.total) * 100)
                                        : null;

                                    return (
                                        <div
                                            key={product.id}
                                            className={styles.productCard}
                                            onClick={() => startQuiz('practice', product.id)}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`${product.name} 연습 시작`}
                                            onKeyDown={(e) => e.key === 'Enter' && startQuiz('practice', product.id)}
                                        >
                                            <p className={styles.productCardName}>{product.name}</p>
                                            <p className={styles.productCardCategory}>{categoryLabel(product.category)}</p>
                                            <div className={styles.productCardBottom}>
                                                <span className={styles.productCardAction}>
                                                    <Icon name="play" style={{ margin: 0, fontSize: '11px', lineHeight: 1 }} /> 연습
                                                </span>
                                                {accuracy !== null && (
                                                    <span className={styles.productProgressMini}>
                                                        <span
                                                            className={styles.progressDot}
                                                            style={{ backgroundColor: getAccuracyColor(accuracy) }}
                                                        />
                                                        <span
                                                            className={styles.progressMiniLabel}
                                                            style={{ color: getAccuracyColor(accuracy) }}
                                                        >
                                                            {accuracy}%
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {isMobile && !selectedCategory && products.length > MOBILE_PER_CATEGORY && (
                                <button
                                    type="button"
                                    className={styles.showAllButton}
                                    onClick={() => { setSelectedCategory(category); window.scrollTo({ top: 0 }); }}
                                >
                                    {categoryLabel(category)} {products.length}개 모두 보기
                                </button>
                            )}
                        </div>
                    ))
                )}

                {/* ===== Recent Activity ===== */}
                {recentAttempts.length > 0 && (
                    <div className={styles.activitySection}>
                        <div className={styles.activityCard}>
                            <div className={styles.activityHeader}>
                                <h4 className={styles.activityTitle}>최근 학습 기록</h4>
                            </div>
                            {recentAttempts.map(attempt => (
                                <div key={attempt.id} className={styles.attemptRow}>
                                    <div className={styles.attemptLeft}>
                                        <div className={attempt.is_correct ? styles.attemptIconCorrect : styles.attemptIconIncorrect}>
                                            <Icon
                                                name={attempt.is_correct ? 'check' : 'close'}
                                                style={{ margin: 0, fontSize: '12px', lineHeight: 1, width: 'auto', height: 'auto' }}
                                            />
                                        </div>
                                        <div className={styles.attemptInfo}>
                                            <div className={styles.attemptProduct}>
                                                {attempt.product_name || `문제 #${attempt.question}`}
                                            </div>
                                            <div className={styles.attemptDate}>
                                                {new Date(attempt.created_at).toLocaleDateString()}
                                            </div>
                                        </div>
                                    </div>
                                    <span
                                        className={styles.attemptResult}
                                        style={{ color: attempt.is_correct ? '#34C759' : '#FF3B30' }}
                                    >
                                        {attempt.is_correct ? '정답' : '오답'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Container>
        </div>
    );
};

export default QuizDashboard;
